import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
	getOrder,
	listBanners,
	listOrders,
	type OrderDetail,
	type OrdersBanner,
	type OrdersOverview,
} from '@/api/medOrders'
// What med-orders' OpenAPI validator accepts as `orderId` (`format: uuid`) and what Sentry's endpoint
// templating collapses to `{id}` — one pattern for both, so anything else is a 400 we don't send.
import { UUID_RE } from '@/sentry/network'

// The Orders section's data (KOVA-797): the order list, the banner feed the renewal card is picked from,
// and the details of each order opened. In-memory only, never persisted (orders are PHI-adjacent and
// change server-side; a reload re-reads).
//
// One rule runs through it (spec §2.7 F7, F8): state changes only by replacing it with a successful
// read. Nothing here mutates an order or a banner locally, a failed read never clears data the user is
// looking at, and an outstanding action never disappears without a read that leaves it out. The
// mobile app gets the same from React Query (`invalidateOrders` = list + details + banners; a failed
// refetch keeps `data`), which is why the triggers below all go through `refreshAll()`.
//
// Actions never throw: the views fire them from lifecycle hooks and event listeners, where a rejection
// would go unhandled. A failure lands in the state (`listState`, `refreshFailed`, `bannersRefreshFailed`,
// a details entry's `state`); it has already been reported to Sentry — by `authedJson` (a transport or HTTP failure)
// or by `medOrders` (a 2xx it cannot read).

/** The list's first-load lifecycle. The skeleton (`loading`) and the error card (`failed`) are for when there is no overview to show. */
export type OrdersListState = 'idle' | 'loading' | 'ready' | 'failed'

/**
 * One order's details. `unavailable` — the order isn't there for this user (a 404, a 400, an id that
 * isn't a UUID): no retry. `failed` — the read broke (5xx, no network): Try again.
 */
export type OrderDetailState = 'loading' | 'ready' | 'failed' | 'unavailable'

export interface OrderDetailEntry {
	state: OrderDetailState
	/** Set exactly when `state` is `ready`. */
	order: OrderDetail | null
	/** A background re-read failed while `order` is shown: keep it, show the stale notice. */
	refreshFailed: boolean
}

/** The tab-return refresh's throttle — the mobile app's foreground refresh (`OrdersPushRefresh.tsx`). */
const VISIBLE_REFRESH_MIN_INTERVAL_MS = 30_000

/** How long after a flow return the one follow-up read waits for the webhook to clear the action (spec §2.7 F6). */
const FOLLOW_UP_REFRESH_DELAY_MS = 6_000

export const useOrdersStore = defineStore('orders', () => {
	const overview = ref<OrdersOverview | null>(null)
	/**
	 * The last banner feed read successfully; `null` until one is. Never cleared by a failed read — the
	 * renewal card stays until a read leaves its action out (spec §2.6 B10).
	 */
	const banners = ref<OrdersBanner[] | null>(null)
	const listState = ref<OrdersListState>('idle')
	/** A background re-read of the list failed while an overview is shown — the stale notice. */
	const refreshFailed = ref(false)
	/**
	 * A background re-read of the feed failed while a feed is shown (a card, or none) — the stale notice too: the
	 * card kept may be one the server has since cleared. Only a successful feed read clears it, never the list's.
	 */
	const bannersRefreshFailed = ref(false)
	/** The list's stale notice: either re-read failed behind what is on screen. Each flag clears with its own read. */
	const stale = computed(() => refreshFailed.value || bannersRefreshFailed.value)
	/** Keyed by order id; one entry per order opened in this session. */
	const details = ref<Record<string, OrderDetailEntry>>({})

	// Latest read wins. Each list read takes the next number and writes only while it is still the newest,
	// so an older answer arriving late can't overwrite a newer one (or flag a failure the newer read
	// already cleared). Same per order id for the details. Mirrors React Query cancelling the in-flight
	// fetch when `invalidateOrders` refetches.
	let listSeq = 0
	const detailSeq = new Map<string, number>()
	/** When the last tab-return refresh ran. Only those count: a mount read doesn't hold one back (mobile parity). */
	let lastVisibleRefreshAt: number | null = null
	let followUpTimer: ReturnType<typeof setTimeout> | null = null

	/**
	 * The list and the banner feed, in parallel. The overview lands as soon as it answers — the feed never
	 * gates it (spec §6.1). An overview failure with an overview on screen flags `refreshFailed` and keeps
	 * it; with nothing on screen it is the error card. A feed failure keeps the last feed (B10) and, when
	 * there is one, flags `bannersRefreshFailed`; before any feed was read it is silent — no card, no notice.
	 * The two flags change together once both reads are in: set one by one, a list answer could clear the
	 * notice that the feed's failure raises again a moment later — the list jumping twice, the alert read twice.
	 */
	async function readList(): Promise<void> {
		const seq = ++listSeq
		const isLatest = () => seq === listSeq
		// What each read leaves its flag at; `undefined` leaves it as it is.
		let listStale: boolean | undefined
		let feedStale: boolean | undefined
		await Promise.all([
			listOrders().then(
				(next) => {
					if (!isLatest()) return
					overview.value = next
					listState.value = 'ready'
					listStale = false
				},
				() => {
					if (!isLatest()) return
					if (overview.value) listStale = true
					else listState.value = 'failed'
				},
			),
			listBanners().then(
				(next) => {
					if (!isLatest()) return
					banners.value = next
					feedStale = false
				},
				() => {
					// The last feed stays (B10); `[]` is a feed read too — no card is what it shows.
					if (isLatest() && banners.value) feedStale = true
				},
			),
		])
		if (!isLatest()) return
		if (listStale !== undefined) refreshFailed.value = listStale
		if (feedStale !== undefined) bannersRefreshFailed.value = feedStale
	}

	/**
	 * Section mount (F1) and the first-load "Try again" (F4). With nothing loaded yet it shows the skeleton;
	 * with an overview it shows that and re-reads in the background — a revisit never blanks the list.
	 */
	function loadList(): Promise<void> {
		if (!overview.value) listState.value = 'loading'
		return readList()
	}

	/**
	 * Re-read the list and the feed without the skeleton: the stale notice's "Try again" (F4), a flow
	 * return (F5) and the triggers below. Keeps everything on a failure (F8); with nothing loaded, a list
	 * failure ends in `failed` — also when it overtook a first load. Cached details are `refreshOrder`'s.
	 */
	function refreshAll(): Promise<void> {
		return readList()
	}

	async function readOrder(id: string): Promise<void> {
		const seq = (detailSeq.get(id) ?? 0) + 1
		detailSeq.set(id, seq)
		try {
			const order = await getOrder(id)
			if (detailSeq.get(id) !== seq) return
			// A read that says the order is gone (404 / 400) is still a read: it replaces a cached one.
			details.value[id] = order
				? { state: 'ready', order, refreshFailed: false }
				: { state: 'unavailable', order: null, refreshFailed: false }
		} catch {
			if (detailSeq.get(id) !== seq) return
			const entry = details.value[id]
			if (entry?.order) entry.refreshFailed = true
			else details.value[id] = { state: 'failed', order: null, refreshFailed: false }
		}
	}

	/**
	 * Details mount (F2) and its "Try again". An id that isn't a UUID is `unavailable` without a request
	 * (it can only be a hand-typed or broken link, and it would be a 400 tagged with junk). A cached order
	 * shows at once and is re-read in the background; otherwise the entry is `loading` until the read.
	 */
	function loadOrder(id: string): Promise<void> {
		if (!UUID_RE.test(id)) {
			details.value[id] = { state: 'unavailable', order: null, refreshFailed: false }
			return Promise.resolve()
		}
		if (!details.value[id]?.order) details.value[id] = { state: 'loading', order: null, refreshFailed: false }
		return readOrder(id)
	}

	/** Re-read an order already on screen, keeping it on a failure (its own `refreshFailed`). Nothing cached → nothing to do. */
	function refreshOrder(id: string): Promise<void> {
		return details.value[id]?.order ? readOrder(id) : Promise.resolve()
	}

	/**
	 * `document.visibilitychange` → visible while an Orders route is mounted (F3): the list, the feed and
	 * the open order (`orderId`, when the details are on screen), at most once per 30 s — a tab flicked
	 * back and forth doesn't re-read every time.
	 */
	async function refreshOnVisible(orderId?: string): Promise<void> {
		const now = Date.now()
		if (lastVisibleRefreshAt !== null && now - lastVisibleRefreshAt < VISIBLE_REFRESH_MIN_INTERVAL_MS) return
		lastVisibleRefreshAt = now
		await Promise.all([refreshAll(), orderId ? refreshOrder(orderId) : undefined])
	}

	/**
	 * F6, a web addition (the app gets an `order_refresh` push instead): billing clears an action by
	 * webhook a few seconds after its flow ends, so the read on return can still see it. One more
	 * `refreshAll()` in 6 s if the action `actionId` (the banner whose CTA the user opened) is still in the
	 * feed then. One at a time: a call while one is pending changes nothing. The view decides when to
	 * call it and cancels it on unmount.
	 */
	function scheduleFollowUpRefresh(actionId: string): void {
		if (followUpTimer) return
		followUpTimer = setTimeout(() => {
			followUpTimer = null
			if (banners.value?.some((banner) => banner.id === actionId)) void refreshAll()
		}, FOLLOW_UP_REFRESH_DELAY_MS)
	}

	function cancelFollowUpRefresh(): void {
		if (followUpTimer) clearTimeout(followUpTimer)
		followUpTimer = null
	}

	/**
	 * The renewal action whose CTA the user opened and hasn't come back from yet (F6) — kept here because the
	 * section unmounts when the CTA leaves it. Only the id: never `Banner.url` (the intake link carries a bearer
	 * `session_id`).
	 */
	let openedActionId: string | null = null

	/** The renewal card's CTA was opened: the next return to the list schedules the follow-up read for it. */
	function rememberOpenedAction(actionId: string): void {
		openedActionId = actionId
	}

	/** On a return to the list: the action opened before it, once — a second call gets `null`. */
	function takeOpenedAction(): string | null {
		const actionId = openedActionId
		openedActionId = null
		return actionId
	}

	return {
		overview,
		banners,
		listState,
		refreshFailed,
		bannersRefreshFailed,
		stale,
		details,
		loadList,
		refreshAll,
		loadOrder,
		refreshOrder,
		refreshOnVisible,
		scheduleFollowUpRefresh,
		cancelFollowUpRefresh,
		rememberOpenedAction,
		takeOpenedAction,
	}
})
